import { useState, useRef, useCallback, useEffect } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;

// Stable session ID — fallback for browsers without crypto.randomUUID
function makeUUID() {
  try { return crypto.randomUUID(); } catch (_) {}
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}
const SESSION_ID = (() => {
  try {
    const key = 'paladin_chat_session';
    let id = sessionStorage.getItem(key);
    if (!id) { id = makeUUID(); sessionStorage.setItem(key, id); }
    return id;
  } catch (_) { return makeUUID(); }
})();

const STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  TRANSCRIBING: 'transcribing',
  CHATTING: 'chatting',
  SYNTHESIZING: 'synthesizing',
  SPEAKING: 'speaking',
};

const QUICK_QUESTIONS = [
  'What are your office hours?',
  'How do I file a claim?',
  'What coverage types do you offer?',
  'How do I request a COI?',
  'What states are you licensed in?',
  'How do I schedule a consultation?',
  'What is your phone number?',
  'How do I update my policy?',
];

const STATUS_LABEL = {
  [STATES.IDLE]: null,
  [STATES.RECORDING]: 'Listening…',
  [STATES.TRANSCRIBING]: 'Processing voice…',
  [STATES.CHATTING]: 'Thinking…',
  [STATES.SYNTHESIZING]: 'Preparing audio…',
  [STATES.SPEAKING]: 'Speaking…',
};

// ─── Avatars ──────────────────────────────────────────────────────────────────
function BotAvatar() {
  return (
    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#002db5,#0040e6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="2" width="6" height="13" rx="3" />
        <path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" />
      </svg>
    </div>
  );
}

function UserAvatar() {
  return (
    <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    </div>
  );
}

// ─── Chat bubbles ─────────────────────────────────────────────────────────────
function Bubble({ role, text }) {
  const isUser = role === 'user';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12, flexDirection: isUser ? 'row-reverse' : 'row' }}>
      {isUser ? <UserAvatar /> : <BotAvatar />}
      <div style={{
        maxWidth: '72%',
        padding: '10px 14px',
        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        background: isUser ? '#1e3a8a' : '#f1f5f9',
        color: isUser ? '#fff' : '#1e293b',
        fontSize: 13.5,
        lineHeight: 1.6,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
      }}>
        {text}
      </div>
    </div>
  );
}

// ─── Typing dots ──────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}>
      <BotAvatar />
      <div style={{ padding: '12px 16px', background: '#f1f5f9', borderRadius: '18px 18px 18px 4px', display: 'flex', gap: 5, alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#94a3b8', display: 'inline-block', animation: `va-dot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

// ─── Waveform canvas ──────────────────────────────────────────────────────────
function WaveformBars({ state, analyserRef }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const barCount = 20;
    const barW = 3;
    const gap = (W - barCount * barW) / (barCount + 1);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      if (state === STATES.RECORDING && analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        for (let i = 0; i < barCount; i++) {
          const value = data[Math.floor((i / barCount) * data.length)] / 255;
          const barH = Math.max(4, value * H);
          ctx.fillStyle = `rgba(220,38,38,${0.5 + value * 0.5})`;
          ctx.beginPath();
          ctx.roundRect(gap + i * (barW + gap), (H - barH) / 2, barW, barH, 2);
          ctx.fill();
        }
      } else if (state === STATES.SPEAKING) {
        const t = Date.now() / 400;
        for (let i = 0; i < barCount; i++) {
          const value = 0.3 + 0.55 * Math.abs(Math.sin(t + i * 0.45));
          const barH = Math.max(4, value * H);
          ctx.fillStyle = `rgba(0,45,181,${0.35 + value * 0.5})`;
          ctx.beginPath();
          ctx.roundRect(gap + i * (barW + gap), (H - barH) / 2, barW, barH, 2);
          ctx.fill();
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [state, analyserRef]);

  return <canvas ref={canvasRef} width={200} height={36} style={{ display: 'block' }} />;
}

// ─── Main widget ──────────────────────────────────────────────────────────────
export default function VoiceAgentWidget() {
  const [open, setOpen] = useState(false);
  const [panelSize, setPanelSize] = useState('normal');
  const [voiceState, setVoiceState] = useState(STATES.IDLE);
  const [history, setHistory] = useState([]);
  const [streamingText, setStreamingText] = useState('');
  const [textInput, setTextInput] = useState('');
  const [error, setError] = useState('');
  const [showQuickQ, setShowQuickQ] = useState(true);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const currentAudioRef = useRef(null);
  const historyEndRef = useRef(null);
  const textInputRef = useRef(null);

  const busy = voiceState !== STATES.IDLE;
  const isSpinning = [STATES.TRANSCRIBING, STATES.CHATTING, STATES.SYNTHESIZING].includes(voiceState);
  const showWaveform = [STATES.RECORDING, STATES.SPEAKING].includes(voiceState);
  const statusLabel = STATUS_LABEL[voiceState];

  const panelW = panelSize === 'maximized' ? 640 : 400;
  const panelH = panelSize === 'minimized' ? 'auto' : panelSize === 'maximized' ? 'min(82vh,820px)' : 600;

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, streamingText]);

  const stopEverything = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      try { mediaRecorderRef.current?.stop(); } catch (_) {}
    }
    if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
    analyserRef.current = null;
    clearTimeout(silenceTimerRef.current);
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
  }, []);

  useEffect(() => { return () => stopEverything(); }, [stopEverything]);

  const streamChat = useCallback(async (message, withAudio = true, inputType = 'text') => {
    setVoiceState(STATES.CHATTING);
    setStreamingText('');
    setShowQuickQ(false);

    try {
      const cRes = await fetch(`${API}/api/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          history: history.map((m) => ({ role: m.role, content: m.content })),
          sessionId: SESSION_ID,
          inputType,
        }),
      });

      let fullResponse = '';
      const reader = cRes.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const lines = decoder.decode(value, { stream: true }).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;
          try {
            const { text, error: sErr } = JSON.parse(payload);
            if (sErr) throw new Error(sErr);
            if (text) { fullResponse += text; setStreamingText(fullResponse); }
          } catch (_) {}
        }
      }

      setHistory((prev) => [...prev, { role: 'assistant', content: fullResponse }]);
      setStreamingText('');

      if (withAudio && fullResponse) {
        setVoiceState(STATES.SYNTHESIZING);
        const sRes = await fetch(`${API}/api/voice/synthesize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: fullResponse }),
        });
        if (sRes.ok) {
          const audioBlob = await sRes.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          currentAudioRef.current = audio;
          setVoiceState(STATES.SPEAKING);
          audio.onended = () => { URL.revokeObjectURL(audioUrl); currentAudioRef.current = null; setVoiceState(STATES.IDLE); };
          audio.onerror = () => { currentAudioRef.current = null; setVoiceState(STATES.IDLE); };
          audio.play();
          return;
        }
      }
      setVoiceState(STATES.IDLE);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setVoiceState(STATES.IDLE);
    }
  }, [history]);

  const sendTextMessage = useCallback(async (text) => {
    const msg = (text || textInput).trim();
    if (!msg || busy) return;
    setError('');
    setTextInput('');
    setHistory((prev) => [...prev, { role: 'user', content: msg }]);
    await streamChat(msg, true);
  }, [textInput, busy, streamChat]);

  const stopRecording = useCallback(() => {
    clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const handleRecordingStop = useCallback(async () => {
    setVoiceState(STATES.TRANSCRIBING);
    if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
    analyserRef.current = null;

    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    if (blob.size < 1000) {
      setError('Recording was too short. Please try again.');
      setVoiceState(STATES.IDLE);
      return;
    }
    setError('');
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'audio.webm');
      const tRes = await fetch(`${API}/api/voice/transcribe`, { method: 'POST', body: formData });
      const { transcript, error: tErr } = await tRes.json();
      if (tErr || !transcript?.trim()) {
        setError(tErr || "Couldn't hear that clearly. Please try again.");
        setVoiceState(STATES.IDLE);
        return;
      }
      const msg = transcript.trim();
      setHistory((prev) => [...prev, { role: 'user', content: msg }]);
      await streamChat(msg, true, 'voice');
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      setVoiceState(STATES.IDLE);
    }
  }, [streamChat]);

  const startRecording = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);

      const checkSilence = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        if (avg < 4) {
          silenceTimerRef.current = setTimeout(() => {
            if (mediaRecorderRef.current?.state === 'recording') stopRecording();
          }, 6000);
        } else {
          clearTimeout(silenceTimerRef.current);
        }
        if (mediaRecorderRef.current?.state === 'recording') requestAnimationFrame(checkSilence);
      };

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      audioChunksRef.current = [];
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = handleRecordingStop;
      mediaRecorderRef.current.start(100);
      setVoiceState(STATES.RECORDING);
      requestAnimationFrame(checkSilence);
    } catch {
      setError('Microphone access denied. Please allow microphone and try again.');
      setVoiceState(STATES.IDLE);
    }
  }, [stopRecording, handleRecordingStop]);

  const handleMicClick = useCallback(() => {
    if (voiceState === STATES.IDLE) return startRecording();
    if (voiceState === STATES.RECORDING) return stopRecording();
    if (voiceState === STATES.SPEAKING) {
      if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
      setVoiceState(STATES.IDLE);
    }
  }, [voiceState, startRecording, stopRecording]);

  const clearHistory = useCallback(() => {
    setHistory([]); setStreamingText(''); setError(''); setTextInput('');
    setShowQuickQ(true); setVoiceState(STATES.IDLE); stopEverything();
  }, [stopEverything]);

  return (
    <>
      <style>{`
        @keyframes va-fab-pulse { 0%,100%{box-shadow:0 4px 20px rgba(0,45,181,.4)}50%{box-shadow:0 4px 32px rgba(0,45,181,.7)} }
        @keyframes va-slide-up  { from{opacity:0;transform:translateY(14px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes va-dot       { 0%,80%,100%{transform:scale(0);opacity:.3} 40%{transform:scale(1);opacity:1} }
        @keyframes va-spin      { to{transform:rotate(360deg)} }
        @keyframes va-pulse-red { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.4)} 50%{box-shadow:0 0 0 8px rgba(239,68,68,0)} }
        @keyframes va-pulse-blue{ 0%,100%{box-shadow:0 0 0 0 rgba(0,45,181,.35)} 50%{box-shadow:0 0 0 8px rgba(0,45,181,0)} }
        .va-input::placeholder{color:#94a3b8}
        .va-input{background:#f8fafc;border:1.5px solid #e2e8f0;color:#1e293b;outline:none;transition:border .2s,box-shadow .2s}
        .va-input:focus{border-color:#002db5;box-shadow:0 0 0 3px rgba(0,45,181,.08)}
        .va-qq:hover{background:#f1f5f9!important;border-color:#cbd5e1!important}
        .va-icon-btn{background:none;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:8px;padding:5px;transition:background .15s;color:#64748b}
        .va-icon-btn:hover{background:#f1f5f9}
        .va-send:hover:not(:disabled){background:#1d4ed8!important}
        .va-mic:hover:not(:disabled){filter:brightness(1.1)}
        .va-conv::-webkit-scrollbar{width:4px}
        .va-conv::-webkit-scrollbar-track{background:transparent}
        .va-conv::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:2px}
      `}</style>

      {/* ── FAB ── */}
      <button
        onClick={() => { setOpen((o) => !o); if (!open) setPanelSize('normal'); }}
        title="Paladin Assistant"
        style={{ position: 'fixed', bottom: 24, right: 24, width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#002db5,#0040e6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, animation: 'va-fab-pulse 2.5s ease-in-out infinite', transition: 'transform .2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        {open
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        }
      </button>

      {/* ── Panel ── */}
      {open && (
        <div style={{ position: 'fixed', bottom: 90, right: 24, width: panelW, height: panelH, borderRadius: 20, background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.15)', zIndex: 9998, display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'va-slide-up .22s ease-out', fontFamily: 'system-ui,-apple-system,sans-serif', transition: 'width .25s ease, height .25s ease', border: '1px solid #e2e8f0' }}>

          {/* ── Header ── */}
          <div style={{ padding: '14px 16px', borderBottom: panelSize === 'minimized' ? 'none' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, background: '#fff', flexShrink: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#002db5,#0040e6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2" width="6" height="13" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 15 }}>Paladin Assistant</div>
              {panelSize !== 'minimized' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                  <span style={{ color: '#64748b', fontSize: 11 }}>Online · Insurance support</span>
                </div>
              )}
            </div>

            {/* Window controls */}
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {panelSize !== 'minimized' && history.length > 0 && (
                <button className="va-icon-btn" onClick={clearHistory} title="Clear chat">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></svg>
                </button>
              )}
              {/* Minimize */}
              <button className="va-icon-btn" title={panelSize === 'minimized' ? 'Restore' : 'Minimize'} onClick={() => setPanelSize(s => s === 'minimized' ? 'normal' : 'minimized')}>
                {panelSize === 'minimized'
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="18 15 12 9 6 15" /></svg>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                }
              </button>
              {/* Maximize */}
              {panelSize !== 'minimized' && (
                <button className="va-icon-btn" title={panelSize === 'maximized' ? 'Restore' : 'Maximize'} onClick={() => setPanelSize(s => s === 'maximized' ? 'normal' : 'maximized')}>
                  {panelSize === 'maximized'
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="10" y1="14" x2="21" y2="3" /><line x1="3" y1="21" x2="14" y2="10" /></svg>
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                  }
                </button>
              )}
              {/* Close */}
              <button className="va-icon-btn" title="Close" onClick={() => setOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          {panelSize !== 'minimized' && (
            <>
              {/* Conversation */}
              <div className="va-conv" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', background: '#fafafa' }}>

                {/* Date stamp */}
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <span style={{ background: '#e2e8f0', color: '#64748b', fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>Today</span>
                </div>

                {/* Greeting when empty */}
                {history.length === 0 && !streamingText && (
                  <>
                    <Bubble role="assistant" text="Hi there! 👋 I'm Paladin's virtual assistant. I'm here to help with all your insurance questions." />
                    <Bubble role="assistant" text="You can type a message, tap a quick question below, or use the mic to speak." />
                  </>
                )}

                {history.map((msg, i) => <Bubble key={i} role={msg.role} text={msg.content} />)}
                {isSpinning && !streamingText && <TypingDots />}
                {streamingText && <Bubble role="assistant" text={streamingText} />}
                <div ref={historyEndRef} />
              </div>

              {/* Waveform bar */}
              {showWaveform && (
                <div style={{ padding: '6px 16px', background: '#fff', display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid #f1f5f9' }}>
                  <WaveformBars state={voiceState} analyserRef={analyserRef} />
                  <span style={{ fontSize: 12, color: voiceState === STATES.RECORDING ? '#dc2626' : '#002db5', fontWeight: 500 }}>{statusLabel}</span>
                </div>
              )}

              {/* Spinner status (non-waveform states) */}
              {isSpinning && (
                <div style={{ padding: '6px 16px', background: '#fff', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ width: 12, height: 12, border: '2px solid #e2e8f0', borderTopColor: '#002db5', borderRadius: '50%', display: 'inline-block', animation: 'va-spin .8s linear infinite' }} />
                  <span style={{ fontSize: 12, color: '#64748b' }}>{statusLabel}</span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ margin: '0 14px 6px', padding: '7px 12px', background: '#fef2f2', borderRadius: 10, color: '#dc2626', fontSize: 12, border: '1px solid #fecaca' }}>
                  {error}
                </div>
              )}

              {/* Quick questions */}
              {showQuickQ && history.length === 0 && (
                <div style={{ padding: '8px 14px', background: '#fff', borderTop: '1px solid #f1f5f9', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {QUICK_QUESTIONS.map((q) => (
                    <button key={q} className="va-qq" onClick={() => sendTextMessage(q)} disabled={busy}
                      style={{ background: '#fff', border: '1.5px solid #e2e8f0', color: '#334155', borderRadius: 20, padding: '5px 12px', fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? .5 : 1, transition: 'background .15s, border-color .15s', fontFamily: 'inherit' }}>
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input row */}
              <div style={{ padding: '10px 14px 14px', background: '#fff', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
                <input
                  ref={textInputRef}
                  className="va-input"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendTextMessage(); } }}
                  placeholder="Ask anything…"
                  disabled={busy}
                  style={{ flex: 1, borderRadius: 24, padding: '10px 16px', fontSize: 13.5, lineHeight: 1.4 }}
                />
                {/* Send */}
                <button className="va-send" onClick={() => sendTextMessage()} disabled={busy || !textInput.trim()} title="Send"
                  style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', background: busy || !textInput.trim() ? '#e2e8f0' : '#002db5', cursor: busy || !textInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .2s' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={busy || !textInput.trim() ? '#94a3b8' : '#fff'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
                {/* Mic */}
                <button className="va-mic" onClick={handleMicClick} disabled={isSpinning} title={voiceState === STATES.RECORDING ? 'Stop' : 'Record voice'}
                  style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: isSpinning ? 'not-allowed' : 'pointer', background: voiceState === STATES.RECORDING ? '#dc2626' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .3s', animation: voiceState === STATES.RECORDING ? 'va-pulse-red 1.4s ease-in-out infinite' : voiceState === STATES.SPEAKING ? 'va-pulse-blue 1.4s ease-in-out infinite' : 'none', opacity: isSpinning ? .5 : 1 }}>
                  {voiceState === STATES.SPEAKING
                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="#002db5"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={voiceState === STATES.RECORDING ? '#fff' : '#475569'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="13" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" /></svg>
                  }
                </button>
              </div>

              {/* Footer */}
              <div style={{ padding: '5px 14px 8px', textAlign: 'center', color: '#cbd5e1', fontSize: 10, background: '#fff', borderTop: '1px solid #f8fafc' }}>
                Paladin Professional Insurance Solutions · 805-692-6900
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
